import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const comparisonTableSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  columns: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        highlighted: z.boolean().default(false).optional(),
      }),
    )
    .min(2)
    .max(5),
  rows: z
    .array(
      z.object({
        feature: z.string().min(1).max(200),
        values: z.array(z.object({ value: z.string().max(120) })).min(2).max(5),
      }),
    )
    .min(1)
    .max(30),
});

export type ComparisonTableData = z.infer<typeof comparisonTableSchema>;

export function ComparisonTableRenderer({ eyebrow, h2, subtitle, columns, rows }: ComparisonTableData) {
  return (
    <section className="v5-section v5-comparison-section">
      <div className="container">
        <div className="v5-section-head">
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-h2">{h2}</h2>
          {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
        </div>
        <div className="v5-comparison-wrap">
          <table className="v5-comparison-table">
            <thead>
              <tr>
                <th aria-hidden></th>
                {columns.map((c, i) => (
                  <th key={i} className={c.highlighted ? 'is-highlighted' : ''}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <th scope="row">{r.feature}</th>
                  {r.values.map((v, j) => {
                    const isCheck = v.value === '✓' || v.value === 'true' || v.value === 'sí' || v.value === 'si';
                    const isCross = v.value === '✗' || v.value === 'false' || v.value === 'no';
                    return (
                      <td key={j} className={isCheck ? 'is-yes' : isCross ? 'is-no' : ''}>
                        {isCheck ? '✓' : isCross ? '—' : v.value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export const comparisonTableBlock: BlockDefinition<ComparisonTableData> = {
  meta: {
    type: 'comparison-table',
    label: 'Tabla comparativa',
    description: 'Comparativa de planes o competencia con filas de características. Marca ✓/✗ automático.',
    category: 'data',
    icon: 'table',
  },
  schema: comparisonTableSchema,
  defaults: {
    eyebrow: 'COMPARATIVA',
    h2: 'Compara los planes',
    columns: [
      { label: 'Starter' },
      { label: 'Pro', highlighted: true },
      { label: 'Enterprise' },
    ],
    rows: [
      {
        feature: 'Usuarios incluidos',
        values: [{ value: '1' }, { value: '5' }, { value: 'Ilimitados' }],
      },
      {
        feature: 'Soporte 24/7',
        values: [{ value: '✗' }, { value: '✓' }, { value: '✓' }],
      },
      {
        feature: 'Integraciones',
        values: [{ value: '5' }, { value: 'Todas' }, { value: 'Custom' }],
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'columns',
      label: 'Columnas',
      type: 'array',
      max: 5,
      itemFields: [
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 80 },
        { name: 'highlighted', label: 'Destacada', type: 'boolean' },
      ],
    },
    {
      name: 'rows',
      label: 'Filas',
      type: 'array',
      max: 30,
      itemFields: [
        { name: 'feature', label: 'Característica', type: 'text', required: true, max: 200 },
        {
          name: 'values',
          label: 'Valores (1 por columna)',
          type: 'array',
          itemFields: [
            { name: 'value', label: 'Valor', type: 'text', required: true, max: 120, helperText: 'Usa ✓ o ✗ para checks.' },
          ],
        },
      ],
    },
  ],
  Renderer: ComparisonTableRenderer,
};
